// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { ExtractProps } from '@Molecules/Details/Details';
import { THForm } from '@Molecules/THForm/THForm';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { AlertOutput, CustomStatusMapType, CustomFieldOutput, PermissionsEnum, UserOutput } from 'thehive-sdk';
import { AlertEventList } from '@Organisms/Alert/eventList';
import { SecurityTagList } from '@Molecules/SecurityTagList/SecurityTagList';
import THTag from '@Atoms/THTag/THTag';
import { FormatDate } from '@Molecules/Format';
import { FormInstance, Select } from 'antd';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { CFList } from '@Molecules/CFList/CFList';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { normalizeDate } from '@Helpers/date';
import { convertToLocaleId } from '@Helpers/locale';
import { CaseDrawerCount } from '@Organisms/Case/CaseDrawerCount/CaseDrawerCount';
import { onOrganisationImageUpload } from '@Helpers/download';
import { PermissionExtract } from '@Hooks/useSplitPermissions';
import { THUserSelector } from '@Molecules/Inputs/THUserSelector/THUserSelector';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { IconLabel } from '@Atoms/Info';

import './extractData.scss';

interface ExtractDataProps {
    data: AlertOutput;
    hasPermissions?: Record<PermissionExtract<[PermissionsEnum.MANAGE_ALERT_UPDATE]>, boolean>;
    eventList?: AlertEventList;
    locale: (label: string | any) => string;
    formEditMode?: boolean;
}

const { OptGroup } = Select;

export const extractData = ({
    data,
    hasPermissions,
    locale,
    formEditMode,
}: ExtractDataProps): Record<any, ExtractProps> => ({
    '#computeSecurity': {
        className: 'alert-drawer__security',
        isForce: false,
        render: (): ReactElement => <SecurityTagList hasPermissions={hasPermissions?.updateAlert || false} />,
    },
    '#computeMetadata': {
        className: 'alert-drawer__metadata',
        isForce: true,
        render: ({ type, source }: { type: string; source: string }) => (
            <div className="alert-drawer__block">
                <THForm.Item label={locale('alert.type')}>
                    <THInput borderless disabled value={type} />
                </THForm.Item>

                <THForm.Item label={locale('alert.source')}>
                    <THInput borderless disabled value={source} />
                </THForm.Item>
            </div>
        ),
    },
    '#computeDate': {
        isForce: true,
        className: 'alert-drawer__date',
        render: ({ reference }: { reference: string }) => (
            <div className="alert-drawer__block">
                <THForm.Item label={locale('alert.reference')}>
                    <THInput borderless disabled value={reference} />
                </THForm.Item>

                <THForm.Item name="date" label={locale('common.occurred-date')} normalize={normalizeDate}>
                    <THDatePicker
                        borderless
                        disabled={!hasPermissions?.updateAlert}
                        placeholder={locale('general.none')}
                        allowClear={false}
                    />
                </THForm.Item>
            </div>
        ),
    },
    '#computeCount': {
        isForce: false,
        className: 'alert-drawer__count',
        render: ({
            observableCount = 0,
            procedureCount = 0,
            similarAlertCount,
            similarCaseCount,
        }: {
            observableCount: number;
            procedureCount: number;
            similarAlertCount: number;
            similarCaseCount: number;
        }) => (
            <CaseDrawerCount
                permissions={[]}
                obsCount={observableCount}
                procedureCount={procedureCount}
                similarAlertsCount={similarAlertCount}
                similarCasesCount={similarCaseCount}
                alertId={data._id}
            />
        ),
    },
    title: {
        className: 'alert-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="title" label={locale('common.title')}>
                <THInput borderless disabled={!hasPermissions?.updateAlert} placeholder={locale('common.title')} />
            </THForm.Item>
        ),
    },
    '#computeAssignee': {
        className: 'alert-drawer__assignee',
        isForce: true,
        render: ({
            assignableUsers,
            refetchAssignableUsers,
        }: {
            assignableUsers?: UserOutput[];
            refetchAssignableUsers: () => void;
        }) => (
            <THForm.Item name="assignee" label={locale('common.assignee')}>
                <THUserSelector
                    placeholder={<IconLabel icon={<UserAvatar name="" />} label={locale('general.unassigned')} />}
                    users={assignableUsers}
                    disabled={!hasPermissions?.updateAlert}
                    onDropdownVisibleChange={(open) => open && refetchAssignableUsers()}
                    allowClear={false}
                />
            </THForm.Item>
        ),
    },
    tags: {
        className: 'alert-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="tags" label={locale('common.tags')}>
                <THTagSelector.Autocomplete
                    borderless
                    disabled={!hasPermissions?.updateAlert}
                    placeholder={locale('common.tags')}
                />
            </THForm.Item>
        ),
    },
    '#computeCase': {
        name: locale('common.case'),
        render: ({ onClick, caseNumber }: any) => <THTag full value={`#${caseNumber}`} textBlack onClick={onClick} />,
    },
    importDate: {
        name: locale('common.import-date'),
        render: (importDate: number) => <FormatDate date={importDate} />,
    },

    description: {
        className: 'alert-drawer__section',
        isForce: true,
        render: () => (
            <THForm.Item name="description" label={locale('task.details.description')}>
                <THMarkdown
                    borderless
                    disabled={!hasPermissions?.updateAlert}
                    onImageUpload={onOrganisationImageUpload}
                    formEditMode={formEditMode}
                />
            </THForm.Item>
        ),
    },
    '#computeStatus': {
        className: 'alert-drawer__resolution-status',
        isForce: false,
        render: ({
            statusList,
            refetchStatusList,
            isImported,
        }: {
            statusList: CustomStatusMapType;
            refetchStatusList: () => void;
            isImported: boolean;
        }) => (
            <>
                <THForm.Item name="status" label={locale('common.status')}>
                    <THSelect
                        borderless
                        onDropdownVisibleChange={(open) => open && refetchStatusList()}
                        disabled={!hasPermissions?.updateAlert || isImported}
                    >
                        {Object.entries(statusList).map(([key, values]) => (
                            <OptGroup key={key} label={key}>
                                {values.map(({ value, colour }) => (
                                    <THSelect.Option key={value} value={value}>
                                        <CustomColorLabel
                                            value={locale({
                                                id: `alert.data.status.${convertToLocaleId(value)}`,
                                                defaultMessage: value,
                                            })}
                                            color={colour}
                                        />
                                    </THSelect.Option>
                                ))}
                            </OptGroup>
                        ))}
                    </THSelect>
                </THForm.Item>

                <THForm.Item name="summary" label={locale('common.summary')}>
                    <THMarkdown
                        borderless
                        disabled={!hasPermissions?.updateAlert}
                        onImageUpload={onOrganisationImageUpload}
                        formEditMode={formEditMode}
                    />
                </THForm.Item>
            </>
        ),
    },
    '#computeCustomFields': {
        className: 'alert-drawer__section',
        isForce: true,
        render: ({
            customFields,
            form,
            activeKeyState,
        }: {
            customFields: CustomFieldOutput[];
            form: FormInstance;
            activeKeyState: {
                activeKey: string;
                setActiveKey: React.Dispatch<React.SetStateAction<string>>;
            };
        }) => (
            <CFList
                form={form}
                customFields={customFields}
                disabled={!hasPermissions?.updateAlert}
                activeKeyState={activeKeyState}
            />
        ),
    },
});
